import Editor from '@draft-js-plugins/editor'
import createMentionPlugin, { defaultSuggestionsFilter, MentionData } from '@draft-js-plugins/mention'
import { EditorState, RichUtils } from 'draft-js'
import { connect } from 'formik'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import MentionComponent from './MentionComponent/MentionComponent'
import SuggestionComponent from './MentionSuggestionComponent/MentionSuggestionComponent'

import 'draft-js/dist/Draft.css'
import '@draft-js-plugins/mention/lib/plugin.css'

import createToolbarPlugin from '@draft-js-plugins/static-toolbar'
// @ts-expect-error
import createSingleLinePlugin from 'draft-js-single-line-plugin'
import { debounce, every, omit } from 'lodash'
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js'

import { Field } from '@/components/Form/Field'

import addMention from './utils/addMention'
import createMentionEntities from './utils/createMentionEntities'

import '@draft-js-plugins/static-toolbar/lib/plugin.css'

import createLinkPlugin from '@draft-js-plugins/anchor'
import { BoldButton, ItalicButton } from '@draft-js-plugins/buttons'

import appendWhiteSpaceToEndOfAllMentions from './utils/appendWhiteSpaceToEndOfAllMentions'

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`

const EditorContainer = styled.div<any>`
  padding: 12px;
  border: ${props => (props.hasFocus ? ' 1px solid #1A56DB' : '1px solid rgb(209 213 219 / 1)')};
  border: 1px solid rgb(209 213 219 / 1);
  ${({ readOnly }: any) => (readOnly ? 'cursor: not-allowed;' : '')}
  outline: none;
  width: 100%;
  border-radius: 0.375rem;
  overflow-y: auto;
  background-color: ${({ readOnly }: any) => (readOnly ? '#F2F2F2' : '')};
  color: var(--wg-grey);

  &:focus {
    border: 1px solid #1a56db;
    outline: none;
  }

  .public-DraftEditorPlaceholder-root {
    color: #8498b6;
  }

  .DraftEditor-root {
    height: 100%;
    width: 100%;
    overflow-y: hidden;
    font-size: 0.875rem;
    font-family:
      Roboto Neue,
      Roboto,
      Arial,
      sans-serif;

    &:focus {
      border: 2px solid #1a56db;
      outline: none;
      padding: 0;
    }

    span {
      vertical-align: middle;
    }

    a {
      color: var(--wg-blue-link);
      text-decoration: underline;
    }
  }
`

const ToolbarContainer = styled.div`
  margin-bottom: 2px;

  .t16lpgj {
    border: 0;
    box-shadow: none;

    input {
      font-size: 13px;
      width: 180px;
      padding: 2px;
    }
    button {
      background-color: #ffffff;
    }
  }
`

interface MentionsRichTextEditorProps {
  autoFocus?: boolean
  className?: string
  name: string
  label?: string
  mentions: MentionData[]
  multiLine?: boolean
  mentionTrigger?: string
  onChange?: (changedValue: string) => void
  setAddMention?: (onAddMention: (mention: any) => void) => void
  placeholder: string
  value?: string
  formik?: any
  children?: any
  hasStaticToolBar?: boolean
  tooltip?: any
  readOnly?: boolean
  onBlur?: (blurredValue: any) => void
}

const MentionsRichTextEditor = ({
  mentions,
  mentionTrigger = '$.',
  setAddMention,
  onChange,
  multiLine,
  placeholder,
  hasStaticToolBar,
  value,
  readOnly,
  onBlur = () => {}
}: MentionsRichTextEditorProps) => {
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState(mentions)

  const buildEditorStateFromMarkdown = (value?: string) => {
    const rawData = markdownToDraft(value?.toString().replaceAll('​', '') || '', { preserveNewlines: true })
    const stateWithEntity = createMentionEntities(rawData, suggestions, value)
    return value ? EditorState.createWithContent(stateWithEntity) : EditorState.createEmpty()
  }
  const [editorState, setEditorState] = useState<EditorState>(buildEditorStateFromMarkdown(value))

  const [hasFocus, setHasFocus] = useState<boolean>(false)
  const { MentionSuggestions, plugins, Toolbar, linkPlugin } = useMemo(() => {
    const singleLinePlugin = createSingleLinePlugin({
      stripEntities: false
    })

    const linkPlugin = createLinkPlugin()
    const toolBarPlugin = createToolbarPlugin()
    const mentionPlugin = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      mentionTrigger: ['@', mentionTrigger],
      mentionComponent(mentionProps) {
        return <MentionComponent {...mentionProps} />
      }
    })
    const { MentionSuggestions } = mentionPlugin
    const { Toolbar } = toolBarPlugin
    const plugins = [mentionPlugin, linkPlugin, toolBarPlugin]
    if (!multiLine) {
      plugins.push(singleLinePlugin)
    }
    return { plugins, MentionSuggestions, linkPlugin, Toolbar }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  const onAddMention = (mention: any) => {
    const newEditorState = addMention(editorState, mention, '', 'IMMUTABLE')
    setEditorState(newEditorState)
    propogateOnChangeEvent(newEditorState)
  }

  useEffect(() => {
    setAddMention && setAddMention(onAddMention)
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [editorState])

  const onOpenChange = useCallback((_open: boolean) => {
    setSuggestionsOpen(_open)
  }, [])

  const onSearchChange = useCallback(({ value }: { value: string }) => {
    setSuggestions(defaultSuggestionsFilter(value, mentions))
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  const propogateOnChangeEvent = useCallback(
    (newState: EditorState) => {
      if (onChange) {
        //we need to ensure each mention does end with whitespace so we know when variable ends.
        const result = draftToMarkdown(appendWhiteSpaceToEndOfAllMentions(newState), {
          escapeMarkdownCharacters: false,
          preserveNewlines: true
        })
        const newValue = value ?? ''
        if (result !== newValue) {
          //append whitespace character to double line break so that linebreaks do not get stripped out with notification renderer
          onChange && onChange(result?.replace(/\n\n/g, `\n\u200B\n`))
        }
      }
    },
    [onChange, value]
  )

  const onEditorChanged = useMemo(() => debounce(propogateOnChangeEvent, 500), [propogateOnChangeEvent])

  const notificationContentChange = (newState: EditorState) => {
    setHasFocus(false)
    setEditorState(newState)
    onEditorChanged(newState)
  }

  const handleKeyCommand = useCallback((command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)

    if (newState) {
      setEditorState(newState)
      return 'handled'
    }

    return 'not-handled'
  }, [])

  const displayInlineToolBar = () => {
    if (hasStaticToolBar) {
      return (
        <ToolbarContainer>
          <Toolbar>
            {externalProps => (
              <div>
                <BoldButton {...externalProps} />
                <ItalicButton {...externalProps} />
                <linkPlugin.LinkButton {...externalProps} />
              </div>
            )}
          </Toolbar>
        </ToolbarContainer>
      )
    }
  }

  return (
    <div className="group/editor relative flex">
      <Wrapper>
        {displayInlineToolBar()}
        <EditorContainer readOnly={readOnly} hasFocus={hasFocus}>
          <Editor
            readOnly={readOnly}
            editorState={editorState}
            onChange={notificationContentChange}
            onBlur={e => {
              setHasFocus(false)
              onBlur && onBlur(e)
            }}
            placeholder={placeholder}
            plugins={plugins}
            handleKeyCommand={handleKeyCommand}
            stripPastedStyles={hasStaticToolBar !== true}
          />
        </EditorContainer>
        <MentionSuggestions
          open={suggestionsOpen}
          onOpenChange={onOpenChange}
          suggestions={suggestions}
          entryComponent={SuggestionComponent}
          onSearchChange={onSearchChange}
        />
      </Wrapper>
      {every([!value, !readOnly, mentionTrigger === '$.', !!suggestions?.length]) && (
        <span className="absolute left-1/2 mx-auto mt-6 translate-y-full -translate-x-1/2 rounded-md bg-gray-100 p-1 text-xs text-gray-500 opacity-0 transition-opacity group-hover/editor:opacity-100">
          Type{' '}
          <kbd className="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs font-bold text-gray-400 hover:cursor-default">
            $.
          </kbd>{' '}
          for variable autocompletion
        </span>
      )}
    </div>
  )
}

interface MentionsRichTextEditorWithFormikProps {
  name: string
  value?: EditorState
  description?: string
  multiline?: boolean
  required?: boolean
  innerRef?: any
  readOnly?: boolean
  label?: string | undefined
  id?: string
  formik?: any
  hasStaticToolBar?: boolean
}

const StyledField = styled(Field)`
  flex: 1;
  width: 100%;

  &.form__field--error {
    > div > div {
      border: 1px solid #d2341c;
    }
  }
`

const MRTWF = memo(
  ({
    mentions,
    placeholder,
    className,
    multiLine = true,
    setAddMention,
    hasStaticToolBar,
    readOnly,
    innerRef,
    ...props
  }: MentionsRichTextEditorWithFormikProps & MentionsRichTextEditorProps) => {
    const fieldProps = omit(props, ['children'])
    const handleChange = useCallback(
      (changedValue: string, formik: any) => {
        if (fieldProps.onChange) {
          fieldProps.onChange(changedValue)
        } else {
          formik.setFieldValue(fieldProps.name, changedValue.replaceAll('`', ''))
        }
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [fieldProps.name]
    )

    const handleBlur = (formik: any) => {
      formik.setFieldTouched(fieldProps.name)
      return (e: any) => {
        if (formik.handleBlur) formik.handleBlur(e)
      }
    }

    return (
      <StyledField
        key={fieldProps.name}
        {...omit(fieldProps, 'onChange')}
        className={className}
        render={({ ...field }) => (
          <div ref={innerRef}>
            <MentionsRichTextEditor
              key={field.name}
              name={field.name}
              value={field.value}
              multiLine={multiLine}
              mentions={mentions}
              readOnly={readOnly}
              setAddMention={setAddMention}
              onChange={(value: string) => handleChange(value, field.formik)}
              onBlur={() => handleBlur(field.formik)}
              placeholder={placeholder}
              hasStaticToolBar={hasStaticToolBar}
            />
          </div>
        )}
      />
    )
  }
)

MRTWF.displayName = 'MRTWF'

const MentionsRichTextEditorWithFormik = connect(MRTWF)

export { MentionsRichTextEditorWithFormik }
